/**
 * @module OverlayActions/Overlay
 */

/*
The overlays in our app fall into 4 categories:
  modal
    full screen overlays that require user interaction

  coachmark (currently part of modal, see OverlayActions/modal openModalCoachmark)
    small dialogs that do not necessarily require interaction displayed at the top center of the viewport (right now these are treated as modals, that will change in the future)

  blueBar
    notifications displayed to the user in a secondary header

  anchored (not yet implemented)
    notifications that need to point to a specific element on the page, any notification that needs to scroll with the page


There are 4 properties that comprise an overlayConfig and the resulting overlay in state
  TYPE - required
    One of the 4 types of overlays, modal/coachmark/anchored/blueBar (right now bluebar and modal are implemented)

  SUBTYPE - optional
    each of the 4 types has a container with a switch statement that determines which component to render for a given overlay object, this is what the switch condition is. e.g. the BROWSER_BAR subtype renders the `<BrowserBar />` component because I added a clause to the switch in `determineModal` in `../containers/Modal`. The switch defaults to a general component of the TYPE, so you can control it through props instead of creating a new component

  PROPS - optional
    this object is passed in as props to the component decided by SUBTYPE
    the overlay component is guaranteed to have an onClose prop that will close the overlay when called

  ID - optional
    This is acts as a reference to your overlay, you can update or close it from the place that created it.
    Only one overlay with a specific ID can exist at a time, a second OPEN_OVERLAY with the same ID will update any existing overlay with that ID
    You only need to set this if you need multiple overlays of a single subtype or you need to close/update the overlay from the component that created it
    If this is not provided, an ID will be generated
      if you have type/subtype/id -> ID = the id you passed in
      if you have type/subtype -> ID = type_subtype
      if you have only type -> ID = type
        NOT RECOMMENDED. This makes usage information less useful and there can only be one per type at a time


Example action:

  openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
    [OVERLAY_CONFIG.SUBTYPE]: 'thumbcount',
    [OVERLAY_CONFIG.PROPS]: {
      message: 'you have thumbed 5 songs',
      background: 'rgb(199, 50, 50)'
    },
  });

  This would create an active blueBar overlay in state with the generated ID 'blueBar_thumbcount'. If the blueBar container determined that this overlay is its highest priority, it would run a switch statement on SUBTYPE which would render a specific component, or default to <BlueBar /> component. The container would then render that component and pass in any props you defined:

  <BlueBar
    message='you have thumbed 5 songs'
    background='rgb(199, 50, 50)'
    onClose={function that will close the blueBar} />

  You could then update that bluebar by dispatching a second OPEN_OVERLAY action with the same ID (in this case the ID 'blueBar_thumbcount' is generated):

  openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
    [OVERLAY_CONFIG.SUBTYPE]: 'thumbcount',
    [OVERLAY_CONFIG.PROPS]: {
      message: 'you have thumbed 6 songs',
      background: 'rgb(199, 50, 50)'
    }
  });

  And finally, you could close the blueBar either by the rendered component calling its onClose prop, or by dispatching:

  closeOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
    [OVERLAY_CONFIG.SUBTYPE]: 'thumbcount'
  });
*/
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { createAction } from 'typesafe-actions';

import AlertToastIcon from '../../static/icons/ic_alert_toast.svg';
import Icon from '../components/Icon/Icon';
import * as Constants from '../constants';
import OverlayActions, { OVERLAY_CONFIG } from '../constants/Overlay';
import * as Utils from '../utils';

export interface OverlayConfig extends $TSFixMe {}

interface CheckAndMaybeOpenOverlayPayload {
  overlayConfig: OverlayConfig;
}

export const openOverlay = createAction(
  OverlayActions.CHECK_AND_MAYBE_OPEN_OVERLAY,
  (overlayConfig: OverlayConfig) => ({ overlayConfig }),
)<CheckAndMaybeOpenOverlayPayload>();
export type CheckAndMaybeOpenOverlayAction = ReturnType<typeof openOverlay>;

interface OpenOverlayPayload {
  overlayConfig: OverlayConfig;
}

export const openVettedOverlay = createAction(
  OverlayActions.OPEN_OVERLAY,
  (overlayConfig: OverlayConfig) => ({ overlayConfig }),
)<OpenOverlayPayload>();
export type OpenOverlayAction = ReturnType<typeof openVettedOverlay>;

interface CloseOverlayPayload {
  overlayConfig: OverlayConfig;
}

export const closeOverlay = createAction(
  OverlayActions.CLOSE_OVERLAY,
  (_overlayConfig: OverlayConfig | string) => {
    // standard usage is to pass in the same object you used to create the modal
    let overlayConfig = _overlayConfig;
    if (typeof _overlayConfig === 'string') {
      // handle ID/Type as argument
      overlayConfig = {
        [OVERLAY_CONFIG.ID]: _overlayConfig,
      };
    }
    return { overlayConfig };
  },
)<CloseOverlayPayload>();
export type CloseOverlayAction = ReturnType<typeof closeOverlay>;

export const openOfflineNotification = () => {
  const message = (
    <span className='BlueBar__OfflineNotification'>
      <Icon className='icon-alert' glyph={AlertToastIcon} />
      <FormattedMessage
        {...Constants.ServiceErrors.ServiceErrorMessages.offlineNotificationMessage}
      />
    </span>
  );
  const config = {
    [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.blueBar,
    [Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]: Constants.Overlay.OVERLAY_SUBTYPES.OFFLINE,
    [Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
      message,
      background: Constants.Overlay.OFFLINE_NOTIFICATION_BG_COLOR,
    },
  };
  return openOverlay(config);
};

export const closeOfflineNotification = () => {
  const config = {
    [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.blueBar,
    [Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]: Constants.Overlay.OVERLAY_SUBTYPES.OFFLINE,
  };
  return closeOverlay(Utils.Overlay.getOverlayId(config));
};

export const openHamburgerMenu = (links: $TSFixMe, onLinkClicked: $TSFixMe) => {
  const overlayConfig = {
    [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.hamburgerMenu,
    [Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
      isMenuActive: true,
      links,
      onLinkClicked,
    },
  };
  return openOverlay(overlayConfig);
};

export const closeHamburgerMenu = () => {
  const overlayConfig = {
    [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.hamburgerMenu,
    [Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
      isMenuActive: false,
    },
  };
  return closeOverlay(overlayConfig);
};
